import { useCallback, useEffect, useState } from "react";
import { accessRequestApi } from "../../services/accessRequestApi";
import { Card } from "../../components/common/Card";
import { Badge } from "../../components/common/Badge";
import { Spinner } from "../../components/common/Spinner";
import { DataTable } from "../../components/common/DataTable";
import { AdminActionButton } from "../../components/common/AdminActionButton";
import { useToast } from "../../context/ToastContext";

/**
 * Second half of the JIT (just-in-time) admin access flow started in
 * AccessRequestPage.jsx: lets an admin who already holds an active
 * app.JITAccessRequests grant review other admins' pending requests and
 * approve or deny them. Reaching this page at all requires that active
 * grant -- GET /management/access-requests and the decision endpoint are both
 * require_jit=True in api/functions/admin.py, same as every other sensitive
 * admin route, so an admin without one is redirected to AccessRequestPage
 * by apiClient's jit_access_required handling before this ever renders.
 *
 * BOOTSTRAP NOTE: the very first admin has no grant and nobody can approve
 * them here yet (chicken-and-egg) -- that one has to be seeded directly in
 * app.JITAccessRequests (status='Approved', a far-future expires_at).
 */
export function ReviewAccessRequestsPage() {
  const { notify } = useToast();
  const [requests, setRequests] = useState(null);

  const load = useCallback(() => {
    accessRequestApi
      .listPending()
      .then((r) => setRequests(r.requests))
      .catch((err) => {
        setRequests([]);
        notify(err.apiMessage || "Could not load pending access requests", "danger");
      });
  }, [notify]);

  useEffect(() => {
    load();
  }, [load]);

  async function decide(request, approve) {
    try {
      const r = await accessRequestApi.decide(request.request_id, approve, approve ? undefined : "Denied via admin console");
      notify(`Request from ${request.requester_email} ${r.status.toLowerCase()}.`, "success");
      load();
    } catch (err) {
      // 403 forbidden -- trying to decide your own request; 400
      // validation_error -- already decided by someone else in the
      // meantime. Both are real, expected outcomes here, not crashes.
      notify(err.apiMessage || "Could not record decision", "danger");
      load();
    }
  }

  if (!requests) return <Spinner />;

  return (
    <div>
      <h1>Review Access Requests</h1>
      <p style={{ color: "var(--color-text-muted)" }}>
        Pending time-boxed admin access requests. Approving starts an active grant for the requester's requested
        duration, starting now; you cannot approve or deny your own request.
      </p>

      <Card title="Pending Requests" className="section-spacing">
        <DataTable
          rows={requests}
          rowKey="request_id"
          emptyTitle="No pending access requests"
          columns={[
            { key: "requester_email", header: "Requester" },
            { key: "reason", header: "Reason" },
            { key: "requested_minutes", header: "Duration", render: (r) => `${r.requested_minutes} min` },
            { key: "requested_date", header: "Requested", render: (r) => new Date(r.requested_date).toLocaleString() },
            { key: "status", header: "Status", render: (r) => <Badge tone="warning">{r.status}</Badge> },
            {
              key: "actions",
              header: "",
              render: (r) => (
                <div style={{ display: "flex", gap: "var(--space-2)" }}>
                  <AdminActionButton
                    label="Approve"
                    danger={false}
                    confirmBody={<p>Grants {r.requester_email} admin access for {r.requested_minutes} minutes.</p>}
                    onConfirm={() => decide(r, true)}
                  />
                  <AdminActionButton
                    label="Deny"
                    confirmBody={<p>Denies {r.requester_email}'s request for admin access.</p>}
                    onConfirm={() => decide(r, false)}
                  />
                </div>
              ),
            },
          ]}
        />
      </Card>
    </div>
  );
}
